'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { useIntl } from 'react-intl';
import { Stack } from '@packages/shared/src/components/Stack/Stack';
import { SeoElementFragmentFragmentDoc } from '@packages/gql/generated/shopping/SeoElementFragmentFragmentDoc';
import { SeoElementLinkList } from '@packages/modules/src/SeoElementLinkList/SeoElementLinkList';
import { seoElementMessages } from './messages';

/* GraphQL */ `
  fragment SeoElementFragment on SeoData {
    relationLinks {
      alt
      url
    }
    alternateBrands {
      alt
      url
    }
    topLinks {
      alt
      url
    }
    similarLinks {
      alt
      url
    }
  }
`;

export type SeoElementProps = {
  data: FragmentType<typeof SeoElementFragmentFragmentDoc>;
};

/**
 * SeoElement component renders all existing seo links in an apropriate layout.
 * */
export const SeoElement = ({ data }: SeoElementProps) => {
  const { alternateBrands, similarLinks, relationLinks, topLinks } = getFragmentData(
    SeoElementFragmentFragmentDoc,
    data,
  );
  const { formatMessage } = useIntl();

  if (!(alternateBrands || similarLinks || relationLinks || topLinks)) {
    return null;
  }

  return (
    <Stack rowGap={3}>
      {alternateBrands && (
        <SeoElementLinkList
          linkList={alternateBrands}
          headline={formatMessage(seoElementMessages.alternateBrandsHeadline)}
        />
      )}
      {similarLinks && (
        <SeoElementLinkList
          linkList={similarLinks}
          headline={formatMessage(seoElementMessages.similarLinksHeadline)}
        />
      )}
      {relationLinks && (
        <SeoElementLinkList
          linkList={relationLinks}
          headline={formatMessage(seoElementMessages.relationLinksHeadline)}
        />
      )}
      {topLinks && (
        <SeoElementLinkList
          linkList={topLinks}
          headline={formatMessage(seoElementMessages.topLinksHeadline)}
        />
      )}
    </Stack>
  );
};
